import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Badge, Alert } from 'react-bootstrap';
import { Download, XOctagon, XCircle } from 'react-bootstrap-icons';
import { listReports, downloadReportFile, deleteReport, cancelReport } from '../../services/project/reportService';
import Container from '../../layouts/Container';
import PaginatedListTable from '../../components/PaginatedListTable';
import AnchorTag from '../../components/AnchorTag';
import { toast } from 'react-toastify';

const Exports = () => {
  const { projectId } = useParams(); // unused for now but keep for context
  const tableRef = useRef();
  const intervalRef = useRef(null);
  const [hasProcessingReports, setHasProcessingReports] = useState(false);

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor('type', {
      header: 'Type',
      cell: info => {
        const typeValue = info.getValue();
        return typeof typeValue === 'string' ? typeValue : String(typeValue || '—');
      },
    }),
    columnHelper.accessor('projectName', {
      header: 'Project',
    }),
    columnHelper.display({
      id: 'dateRange',
      header: 'Date Range',
      cell: info => {
        const report = info.row.original;
        const params = report.params;
        
        if (!params || !params.startDate || !params.endDate) return '—';
        
        const startDate = new Date(params.startDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: '2-digit'
        });
        const endDate = new Date(params.endDate).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: '2-digit'
        });
        
        return `${startDate} - ${endDate}`;
      },
    }),
    columnHelper.accessor('fileSize', {
      header: 'Size',
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: info => {
        const report = info.row.original;
        const status = report.status;
        const statusText = typeof status === 'string' ? status : String(status || 'unknown');
        const getBadgeVariant = (status) => {
          switch (status?.toLowerCase()) {
            case 'done':
              return 'success';
            case 'queued':
              return 'secondary';
            case 'failed':
              return 'danger';
            case 'processing':
              return 'info';
            default:
              return 'secondary';
          }
        };

        const isProcessing = status?.toLowerCase() === 'processing';
        const progressPercentage = isProcessing && report.progress?.percentage ? `${report.progress.percentage}%` : '';

        return (
          <div className="d-flex align-items-center gap-2">
            <Badge bg={getBadgeVariant(statusText)} className="text-uppercase">
              {statusText}
            </Badge>
            {isProcessing && progressPercentage && (
              <span className="text-muted small">{progressPercentage}</span>
            )}
          </div>
        );
      },
    }),
    // columnHelper.accessor('params', {
    //   header: 'Params',
    //   cell: info => {
    //     const paramsValue = info.getValue();
    //     const paramsText = paramsValue ? JSON.stringify(paramsValue) : '—';
    //     return (
    //       <div style={{ maxWidth: 240, overflowWrap: 'break-word' }}>
    //         {paramsText}
    //       </div>
    //     );
    //   },
    // }),
    // columnHelper.accessor('key', {
    //   header: 'Key',
    //   cell: info => {
    //     const keyValue = info.getValue();
    //     return typeof keyValue === 'string' ? keyValue : String(keyValue || '—');
    //   },
    // }),
    // columnHelper.accessor('error', {
    //   header: 'Error',
    //   cell: info => {
    //     const errorValue = info.getValue();
    //     const errorText = errorValue ? (typeof errorValue === 'string' ? errorValue : String(errorValue)) : '—';
    //     return (
    //       <div style={{ maxWidth: 240, overflowWrap: 'break-word' }}>
    //         {errorText}
    //       </div>
    //     );
    //   },
    // }),
    columnHelper.accessor('createdAt', {
      header: 'Date Created',
      cell: info => info.getValue() ? new Date(info.getValue()).toLocaleString() : '—',
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      meta: { sticky: true },
      cell: info => {
        const report = info.row.original;
        const status = report.status?.toLowerCase();
        const canDownload = report.id && status === 'done';
        const canCancel = status === 'processing' || status === 'queued';
        const canDelete = status === 'done' || status === 'cancelled' || status === 'failed';
        
        return (
          <div className="d-flex gap-2">
            {canDownload && (
              <AnchorTag 
                onClick={() => handleDownloadReport(report.id, report.type)} 
                hoverText='Download'
              >
                <Download />
              </AnchorTag>
            )}
            {canCancel && (
              <AnchorTag 
                onClick={() => handleCancelReport(report.id)} 
                hoverText='Cancel'
              >
                <XCircle />
              </AnchorTag>
            )}
            {canDelete && (
              <AnchorTag 
                onClick={() => handleDeleteReport(report.id)} 
                hoverText='Delete'
              >
                <XOctagon />
              </AnchorTag>
            )}
          </div>
        );
      },
    }),
  ];

  const fetchReports = async (params) => {
    try {
      const data = await listReports({ page: params.pageIndex + 1, limit: params.pageSize });
      
      // Check if there are any processing reports
      const processingExists = data.reports?.some(report => report.status?.toLowerCase() === 'processing');
      setHasProcessingReports(processingExists);
      
      return {
        data: data.reports || [],
        pageCount: data.pagination?.pages || 0,
        totalItems: data.pagination?.total || 0,
      };
    } catch (err) {
      console.error(err);
      toast.error('Failed to load exports');
      return { data: [], pageCount: 0, totalItems: 0 };
    }
  };

  const handleRefresh = () => {
    tableRef.current?.refetch();
  };

  // Auto-refresh when there are processing reports
  useEffect(() => {
    if (hasProcessingReports) {
      intervalRef.current = setInterval(() => {
        tableRef.current?.refetch();
      }, 5000); // 5 seconds
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hasProcessingReports]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleDownloadReport = async (reportId, type) => {
    try {
      const filename = `${type}_${new Date().toISOString().split('T')[0]}.csv`;
      await downloadReportFile(reportId, filename);
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    }
  };

  const handleDeleteReport = async (reportId) => {
    const confirmed = window.confirm('Are you sure you want to delete this report?');
    if (!confirmed) return;

    try {
      await deleteReport(reportId);
      toast.success('Report deleted successfully');
      tableRef.current?.refetch(); // Refresh the table
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete report');
    }
  };

  const handleCancelReport = async (reportId) => {
    const confirmed = window.confirm('Are you sure you want to cancel this report?');
    if (!confirmed) return;

    try {
      await cancelReport(reportId);
      toast.success('Report cancelled successfully');
      tableRef.current?.refetch(); // Refresh the table
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel report');
    }
  };

  return (
    <Container>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">Exports</h2>
        <Button onClick={handleRefresh}>Refresh</Button>
      </div>

      <Alert variant="info" className="mb-3">
        <strong>Report Limit:</strong> You can only have up to 15 reports at a time. 
        Generating a new report will automatically delete the oldest completed report.
      </Alert>

      <PaginatedListTable
        ref={tableRef}
        tableId="exports-list"
        columns={columns}
        rowIdAccessor={row => row.id}
        onFetchData={fetchReports}
      />
    </Container>
  );
};

export default Exports;
